import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDeviceType } from "@/common/hooks";
import { useFeedStore } from "@/common/store/useFeedStore";
import { deviceTypeEnum } from "@/common/utils/device";
import { useScrollStore } from "@/common/store/scrollStore";
import { useMediaQuery } from "usehooks-ts";
import {
  enableSnapFeedState,
  resetSnapFeedState,
  SNAP_ROOT_SELECTOR
} from "@/common/utils/snapFeedUtils";
import { SNAP_SCROLL_OFFSET } from "./snapFeedConstants";

interface IUserSnapFeed {
  withSnap?: boolean;
  previewFeedRef: React.MutableRefObject<HTMLDivElement | null>;
}

export const useSnapFeed = ({ previewFeedRef, withSnap }: IUserSnapFeed) => {
  const scrollElement = useScrollStore((state) => state.scrollElement);
  const isForceSnapFeed = useFeedStore((state) => state.isForceSnapFeed);
  const snapFeedDisabled = useFeedStore((state) => state.snapFeedDisabled);
  const deviceType = useDeviceType();

  const isLandscapeMobile = useMediaQuery("(max-width: 1024px) and (orientation: landscape)");
  const isMobile = useMemo(() => (deviceType === deviceTypeEnum.PHONE) || isLandscapeMobile, [deviceType, isLandscapeMobile]);

  const rootRef = useRef<HTMLElement | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const snapAvailable = useMemo(() => (isMobile && !snapFeedDisabled && withSnap), [isMobile, snapFeedDisabled, withSnap]);

  const enableSnapFeed = useCallback(() => {
    if (!rootRef.current || !snapAvailable) {
      return;
    }

    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }

    setIsTransitioning(true);

    transitionTimeoutRef.current = enableSnapFeedState(rootRef.current, () => {
      transitionTimeoutRef.current = null;
      setIsTransitioning(false);
    });

    if (!transitionTimeoutRef.current) {
      setIsTransitioning(false);
    }
  }, [snapAvailable]);

  const disableSnapFeed = useCallback(() => {
    if (!rootRef.current) {
      return;
    }

    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }

    resetSnapFeedState(rootRef.current);
  }, []);

  useEffect(() => {
    rootRef.current = document.querySelector(SNAP_ROOT_SELECTOR) as HTMLElement;

    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
      disableSnapFeed();
    };
  }, [disableSnapFeed]);

  const rootScrollHandler = useCallback(() => {
    const feed = previewFeedRef.current;

    if (!feed || !snapAvailable || !scrollElement || isTransitioning) {
      return;
    }

    const { top } = feed.getBoundingClientRect();

    if (top <= 0) {
      scrollElement.scrollTo({ top: feed.offsetTop });
      enableSnapFeed();
    }
  }, [snapAvailable, scrollElement, isTransitioning, enableSnapFeed]);

  const feedScrollHandler = useCallback(() => {
    const feed = previewFeedRef.current;

    if (isForceSnapFeed || !feed || !scrollElement || isTransitioning) {
      return;
    }

    if (feed.scrollTop <= 0) {
      disableSnapFeed();
      scrollElement.scrollTo({ top: feed.offsetTop - SNAP_SCROLL_OFFSET });
    }
  }, [isForceSnapFeed, scrollElement, isTransitioning, disableSnapFeed]);

  useEffect(() => {
    if (isForceSnapFeed) {
      enableSnapFeed();
    }
  }, [isForceSnapFeed, enableSnapFeed]);

  useEffect(() => {
    if (!isMobile) {
      disableSnapFeed();
    }
  }, [isMobile]);

  useEffect(() => {
    if (scrollElement) {
      scrollElement.addEventListener("scroll", rootScrollHandler);
    }
    return () => scrollElement?.removeEventListener("scroll", rootScrollHandler);
  }, [rootScrollHandler]);

  useEffect(() => {
    const feedContainer = previewFeedRef.current;

    if (feedContainer) {
      feedContainer.addEventListener("scroll", feedScrollHandler);
    }
    return () => feedContainer?.removeEventListener("scroll", feedScrollHandler);
  }, [feedScrollHandler]);
};
